import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { APP_PATH, BRAND, realVisitorsLink, REMOVAL_EMAIL, signUpPath } from '../lib/brand.ts';
import { REAL_USERS_PUBLIC } from '../lib/features.ts';
import { CookieSettingsLink } from './CookieBanner.tsx';

/**
 * Frame of the public pages: no sign-in needed to read them, no sidebar. `site`: the landing's look (landing.css),
 * for the pages that sell (the landing, /real-visitors); reports and legal pages keep the plain frame.
 */
export function PublicShell({ children, dark = false, host, site = false }: { children: ReactNode; dark?: boolean; host?: string; site?: boolean }) {
  if (site) return <SiteShell>{children}</SiteShell>;
  return (
    <div className={dark ? 'public public-dark-top' : 'public'}>
      <header className="public-bar">
        <Link to="/" className="public-brand">
          {BRAND}
        </Link>
        <nav className="public-nav" aria-label="Site">
          <Link className="nav-extra" to="/#how">
            How it works
          </Link>
          <Link className="nav-pricing" to="/#pricing">
            Pricing
          </Link>
          <Link className="nav-extra" to="/#faq">
            FAQ
          </Link>
          <Link className="nav-docs" to="/docs">
            Docs
          </Link>
          <Link to={APP_PATH}>Sign in</Link>
          <Link className="button brand" to={signUpPath()}>
            Sign up
          </Link>
        </nav>
      </header>
      <main className="public-main">{children}</main>
      <footer className="public-foot">
        <p>
          Powered by{' '}
          <Link to="/" className="powered-brand">
            {BRAND}
          </Link>
          : AI visitors test your site before your users do.
        </p>
        {host && REMOVAL_EMAIL && (
          <p>
            Do you own {host}?{' '}
            <a href={`mailto:${REMOVAL_EMAIL}?subject=${encodeURIComponent(`Remove the report about ${host}`)}&body=${encodeURIComponent(window.location.href)}`}>
              Ask us to remove this report
            </a>
            .
          </p>
        )}
        <nav className="foot-links" aria-label="Product">
          <Link to="/#how">How it works</Link>
          {REAL_USERS_PUBLIC && <Link to={realVisitorsLink('footer')}>Real visitors</Link>}
          <Link to="/#pricing">Pricing</Link>
          <Link to="/#faq">FAQ</Link>
          <Link to="/docs">Docs</Link>
        </nav>
        <nav className="legal-links" aria-label="Legal">
          <Link to="/terms">Terms</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/cookies">Cookies</Link>
          <Link to="/dpa">DPA</Link>
          <Link to="/subprocessors">Sub-processors</Link>
          <CookieSettingsLink />
        </nav>
      </footer>
    </div>
  );
}

function SiteShell({ children }: { children: ReactNode }) {
  return (
    <div className="lx">
      <header className="lx-bar">
        <nav aria-label="Site">
          <Link to="/" className="public-brand">
            {BRAND}
          </Link>
          <div className="lx-bar-mid">
            <Link to="/#how">How it works</Link>
            <Link to="/#pricing">Pricing</Link>
            <Link to="/#faq">FAQ</Link>
            <Link to="/docs">Docs</Link>
          </div>
          <div className="lx-bar-end">
            <Link className="signin" to={APP_PATH}>
              Sign in
            </Link>
            <Link className="lx-btn brand small" to={signUpPath()}>
              Sign up
            </Link>
          </div>
        </nav>
      </header>
      <main>{children}</main>
      <footer className="lx-band lx-foot">
        <div className="lx-frame">
          <div className="lx-foot-cols">
            <div className="lx-foot-about">
              <Link to="/" className="public-brand">
                {BRAND}
              </Link>
              <span>AI visitors test your site before your users do.</span>
            </div>
            <nav aria-label="Product">
              <strong>Product</strong>
              <Link to="/#how">How it works</Link>
              {REAL_USERS_PUBLIC && <Link to={realVisitorsLink('footer')}>Real visitors</Link>}
              <Link to="/#pricing">Pricing</Link>
              <Link to="/#faq">FAQ</Link>
              <Link to="/docs">Docs</Link>
            </nav>
            <nav aria-label="Legal">
              <strong>Legal</strong>
              <Link to="/terms">Terms</Link>
              <Link to="/privacy">Privacy</Link>
              <Link to="/cookies">Cookies</Link>
              <Link to="/dpa">DPA</Link>
              <Link to="/subprocessors">Sub-processors</Link>
              <CookieSettingsLink />
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
